'use client';

import { useSyncExternalStore } from 'react';

/**
 * "Verder op je telefoon" (QR-overdracht) heeft alleen zin achter een laptop
 * of desktop. Op een telefoon zit de klant al op het toestel met de foto's.
 *
 * Een telefoon herkennen we aan twee dingen tegelijk:
 * - de primaire aanwijzer is grof (een vinger), en
 * - het kleinste scherm-formaat is klein.
 *
 * Het scherm-formaat (`screen.width/height`) in plaats van alleen de
 * viewport-breedte, omdat een telefoon in liggende stand breder is dan 768 px
 * maar nog steeds een telefoon is. Een tablet (kortste zijde 768 px of meer)
 * houdt de knop: daar is overzetten vanaf een telefoon wel nuttig. Een smal
 * desktopvenster met een muis houdt hem ook.
 */
export const HANDHELD_MAX_SHORT_SIDE_PX = 767;

export type HandheldSignals = {
  coarsePointer: boolean;
  hoverNone: boolean;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
};

export function isHandheldFromSignals(signals: HandheldSignals): boolean {
  const touchFirst = signals.coarsePointer || signals.hoverNone;
  if (!touchFirst) return false;
  const shortSide = Math.min(
    signals.screenWidth || Number.POSITIVE_INFINITY,
    signals.screenHeight || Number.POSITIVE_INFINITY,
  );
  if (Number.isFinite(shortSide)) return shortSide <= HANDHELD_MAX_SHORT_SIDE_PX;
  return signals.viewportWidth > 0 && signals.viewportWidth <= HANDHELD_MAX_SHORT_SIDE_PX;
}

function readHandheld(): boolean {
  if (typeof window === 'undefined') return false;
  const media = (query: string) => {
    try {
      return Boolean(window.matchMedia?.(query).matches);
    } catch {
      return false;
    }
  };
  return isHandheldFromSignals({
    coarsePointer: media('(pointer: coarse)'),
    hoverNone: media('(hover: none)'),
    screenWidth: Number(window.screen?.width || 0),
    screenHeight: Number(window.screen?.height || 0),
    viewportWidth: Number(window.innerWidth || 0),
  });
}

function subscribe(onChange: () => void) {
  if (typeof window === 'undefined') return () => {};
  const queries = ['(pointer: coarse)', '(hover: none)']
    .map((query) => {
      try {
        return window.matchMedia?.(query) ?? null;
      } catch {
        return null;
      }
    })
    .filter((query): query is MediaQueryList => Boolean(query));
  queries.forEach((query) => query.addEventListener?.('change', onChange));
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  return () => {
    queries.forEach((query) => query.removeEventListener?.('change', onChange));
    window.removeEventListener('resize', onChange);
    window.removeEventListener('orientationchange', onChange);
  };
}

/**
 * `true` op een telefoon. Tijdens server-rendering en hydratatie `false`, zodat
 * de HTML gelijk blijft; direct daarna volgt de echte waarde.
 */
export function useIsHandheldDevice(): boolean {
  return useSyncExternalStore(subscribe, readHandheld, () => false);
}
